/**
 * Hulk Form Builder 統合 - 汎用 Hulk Form ページ
 *
 * Hulk Form は iframe で読み込まれるため、親ページから直接アクセスできません。
 * postMessage API を使って iframe との通信を行い、フォーム送信成功を検知します。
 *
 * ## フロー
 * 1. iframe の URL に customer_id と hidden_customer_tag を追加
 * 2. iframe 内のカスタムスクリプトが送信成功を検知（.after_form_submit の表示）
 * 3. iframe から postMessage で親ページに通知
 * 4. 親ページで Cookie をセット（hidden_customer_tag の値で保存）
 * 5. 次回アクセス時にフォームを非表示
 *
 * ## セキュリティ
 * - origin を完全一致で検証（https://formbuilder.hulkapps.com）
 * - event.source が iframe の contentWindow と一致することを確認
 * - 送信成功時のみ Cookie を保存（バリデーションエラーや通信失敗では保存しない）
 *
 * @see /HULK_FORM_INTEGRATION.md - 設計書（Hulk Form 側のスクリプト含む）
 * @see /src/assets/js/helpers/surveyFormHelper.js - 共通ヘルパー関数
 * @see /shopify/templates/page.hulk-form.liquid - Liquid テンプレート
 */

import {
  hideFormIfCookie,
  setSurveyCompletedCookie,
} from '../../helpers/surveyFormHelper'

const ID_HULK_FORM = 'js-hulk-form'
const FORM_ID = 'hulk_form'

/* eslint-disable no-console */
document.addEventListener('DOMContentLoaded', async () => {
  console.log('[Hulk Form] Initializing hulk form page')

  // 1. 要素の取得
  const elementHulkForm = document.getElementById(ID_HULK_FORM)
  if (!elementHulkForm) {
    console.warn('[Hulk Form] Container element not found, exiting')
    return
  }

  const { customerId, hiddenCustomerTag } = elementHulkForm.dataset
  console.log('[Hulk Form] Customer ID:', customerId)
  console.log('[Hulk Form] Hidden customer tag:', hiddenCustomerTag)

  // 2. Cookie があればフォーム非表示
  if (hiddenCustomerTag) {
    const isHidden = hideFormIfCookie({
      cookieTag: hiddenCustomerTag,
      formId: FORM_ID,
      afterSubmitId: 'hulk_form_after_submit',
    })
    if (isHidden) {
      console.log('[Hulk Form] Form hidden by cookie, exiting')
      return
    }
  }

  // 3. Cookie 設定関数
  const setCookie = () => {
    if (hiddenCustomerTag) {
      console.log('[Hulk Form] Setting survey completed cookie')
      setSurveyCompletedCookie(hiddenCustomerTag)
    }
  }

  // 4. iframe の参照を保持
  let iframeContentWindow = null
  const getIframeWindow = () => {
    const formElement = document.getElementById(FORM_ID)
    const iframe = formElement?.querySelector('iframe')
    return iframe?.contentWindow || null
  }

  // 5. postMessage API で iframe からのメッセージを監視
  console.log('[Hulk Form] Setting up postMessage listener')
  window.addEventListener('message', (event) => {
    const data = event.data

    // hulkFormSubmit メッセージのみをログ出力（デバッグ用）
    if (data && data.type === 'hulkFormSubmit') {
      console.log('[Hulk Form] hulkFormSubmit message received:', {
        origin: event.origin,
        source: event.source,
      })
    }

    // セキュリティチェック1: origin を完全一致で確認（部分一致は危険）
    const allowedOrigin = 'https://formbuilder.hulkapps.com'
    if (event.origin !== allowedOrigin) {
      return
    }

    // セキュリティチェック2: event.source が実際の iframe の contentWindow と一致するか確認
    if (!iframeContentWindow) {
      iframeContentWindow = getIframeWindow()
    }
    if (event.source !== iframeContentWindow) {
      console.warn('[Hulk Form] Message source does not match iframe')
      return
    }

    // フォーム送信完了メッセージをチェック
    if (
      data &&
      (data.type === 'hulkFormSubmit' ||
        data.type === 'formSubmit' ||
        data.type === 'submit')
    ) {
      console.log('[Hulk Form] Form submission detected, setting cookie')
      setCookie()
    }
  })

  // 6. iframe に customer_id と hidden_customer_tag を追加（MutationObserver で監視）
  let isIframeSetup = false

  const setupIframe = (iframe) => {
    if (!iframe || isIframeSetup) {
      return
    }

    // iframe に src が設定されているか確認
    if (!iframe.src) {
      return
    }

    // iframe の src にパラメータを追加
    try {
      const url = new URL(iframe.src)
      let updated = false

      if (customerId && !url.searchParams.has('customer_id')) {
        url.searchParams.set('customer_id', customerId)
        updated = true
      }

      if (hiddenCustomerTag && !url.searchParams.has('hidden_customer_tag')) {
        url.searchParams.set('hidden_customer_tag', hiddenCustomerTag)
        updated = true
      }

      if (updated) {
        iframe.src = url.toString()
        console.log('[Hulk Form] Added parameters to iframe URL')
        isIframeSetup = true
        // iframe の参照を保持（postMessage 検証用）
        iframeContentWindow = iframe.contentWindow
      } else {
        console.log('[Hulk Form] Parameters already in URL or not available')
        isIframeSetup = true
        iframeContentWindow = iframe.contentWindow
      }
    } catch (error) {
      console.error('[Hulk Form] Error setting parameters:', error)
    }
  }

  // 初回チェック
  const formElement = document.getElementById(FORM_ID)
  if (formElement) {
    const iframe = formElement.querySelector('iframe')
    if (iframe) {
      setupIframe(iframe)
    }
  }

  // MutationObserver で iframe の追加と src 属性の設定を監視
  if (formElement && !isIframeSetup) {
    const iframeObserver = new MutationObserver((mutations) => {
      if (isIframeSetup) {
        iframeObserver.disconnect()
        return
      }

      for (const mutation of mutations) {
        // 追加されたノードをチェック
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (node.nodeName === 'IFRAME') {
              console.log('[Hulk Form] iframe added to DOM')
              setupIframe(node)
              if (isIframeSetup) {
                iframeObserver.disconnect()
                return
              }
            }
            // iframe が入れ子になっている可能性もチェック
            if (node.nodeType === Node.ELEMENT_NODE) {
              const iframe = node.querySelector('iframe')
              if (iframe) {
                console.log('[Hulk Form] iframe found in added element')
                setupIframe(iframe)
                if (isIframeSetup) {
                  iframeObserver.disconnect()
                  return
                }
              }
            }
          }
        }

        // src 属性の変更をチェック
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'src'
        ) {
          const iframe = mutation.target
          if (iframe.nodeName === 'IFRAME') {
            console.log('[Hulk Form] iframe src attribute changed')
            setupIframe(iframe)
            if (isIframeSetup) {
              iframeObserver.disconnect()
              return
            }
          }
        }
      }
    })

    iframeObserver.observe(formElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src'],
    })
    console.log('[Hulk Form] MutationObserver started for iframe detection')
  }

  console.log('[Hulk Form] Initialization complete')
})
